/**
 * CreateContactForm Component
 *
 * Form for creating a new contact.
 * Includes validation for email and phone fields.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { contactsApi } from '@/services/contactsApi';
import type { CreateContactRequest } from '@/services/contactsApi';
import { isValidEmail, isValidPhone } from '@/utils/validation';

interface FormErrors {
  email?: string;
  phone?: string;
}

export default function CreateContactForm() {
  const { t } = useTranslation('people');
  const navigate = useNavigate();

  // Form state
  const [form, setForm] = useState<CreateContactRequest>({
    first_name: '',
    last_name: '',
    primary_email: '',
    primary_phone: '',
    title: '',
    importance: 'normal',
    notes: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.first_name.trim()) return;

    // Validate before submit
    const newErrors: FormErrors = {};
    if (form.primary_email && !isValidEmail(form.primary_email)) {
      newErrors.email = t('create_contact_form.errors.email_invalid');
    }
    if (form.primary_phone && !isValidPhone(form.primary_phone)) {
      newErrors.phone = t('create_contact_form.errors.phone_invalid');
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const created = await contactsApi.create(form);
      // Navigate to the new contact's detail page
      navigate(`/people/contacts/${created.id}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('errors.failed_to_create', { type: 'contact' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/people/contacts');
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={handleCancel}
          className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          {t('create_contact_form.back')}
        </button>
        <h2 className="text-lg font-semibold text-gray-900">{t('create_contact_form.title')}</h2>
        <div className="w-16" />
      </div>

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {submitError}
        </div>
      )}

      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('create_contact_form.fields.first_name')}
              </label>
              <input
                type="text"
                value={form.first_name}
                onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('create_contact_form.fields.last_name')}
              </label>
              <input
                type="text"
                value={form.last_name || ''}
                onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('create_contact_form.fields.email')}
              </label>
              <input
                type="email"
                value={form.primary_email || ''}
                onChange={(e) => {
                  setForm({ ...form, primary_email: e.target.value });
                  if (errors.email) setErrors({ ...errors, email: undefined });
                }}
                onBlur={() => {
                  if (form.primary_email && !isValidEmail(form.primary_email)) {
                    setErrors({ ...errors, email: t('create_contact_form.errors.email_invalid') });
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.email ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('create_contact_form.fields.phone')}
              </label>
              <input
                type="tel"
                value={form.primary_phone || ''}
                onChange={(e) => {
                  setForm({ ...form, primary_phone: e.target.value });
                  if (errors.phone) setErrors({ ...errors, phone: undefined });
                }}
                onBlur={() => {
                  if (form.primary_phone && !isValidPhone(form.primary_phone)) {
                    setErrors({ ...errors, phone: t('create_contact_form.errors.phone_invalid') });
                  }
                }}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.phone ? 'border-red-300' : 'border-gray-300'
                }`}
              />
              {errors.phone && (
                <p className="mt-1 text-sm text-red-600">{errors.phone}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('create_contact_form.fields.job_title')}
              </label>
              <input
                type="text"
                value={form.title || ''}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('create_contact_form.fields.importance')}
              </label>
              <select
                value={form.importance}
                onChange={(e) =>
                  setForm({
                    ...form,
                    importance: e.target.value as 'vip' | 'high' | 'normal' | 'low',
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="vip">{t('create_contact_form.importance_options.vip')}</option>
                <option value="high">{t('create_contact_form.importance_options.high')}</option>
                <option value="normal">{t('create_contact_form.importance_options.normal')}</option>
                <option value="low">{t('create_contact_form.importance_options.low')}</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('create_contact_form.fields.notes')}
            </label>
            <textarea
              value={form.notes || ''}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              {t('create_contact_form.actions.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !form.first_name.trim() || Object.keys(errors).length > 0}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('create_contact_form.actions.creating') : t('create_contact_form.actions.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
